import { useCallback } from 'react';
import api from '../utils/api';

export interface SessionData {
  session_id: string;
  user_id?: number;
  user_type?: 'dealer' | 'anonymous';
  device_type?: string;
  browser?: string;
  os?: string;
  screen_resolution?: string;
  viewport_size?: string;
  language?: string;
  timezone?: string;
  ip_address?: string;
  user_agent?: string;
  referrer?: string;
  entry_page?: string;
  country?: string;
  region?: string;
  city?: string;
  lat?: number;
  lng?: number;
  login_at?: string;
  logout_at?: string;
  session_duration?: number;
  pages_viewed?: number;
  interactions_count?: number;
  is_bounce?: boolean;
  last_activity?: string;
}

export interface PageViewData {
  session_id: string;
  page_url: string;
  page_title?: string;
  page_path: string;
  page_type: 'home' | 'list' | 'detail' | 'chat' | 'admin' | 'login' | 'register' | 'settings' | 'other';
  item_type?: 'boat' | 'motor';
  item_id?: number;
  referrer_url?: string;
  referrer_type?: 'direct' | 'search' | 'social' | 'internal' | 'external';
  entrance_time: string;
  exit_time?: string;
  time_on_page?: number;
  scroll_depth?: number;
  is_bounce?: boolean;
}

export interface TrackingEvent {
  event_name: string;
  event_category: 'engagement' | 'conversion' | 'navigation' | 'error' | 'system' | 'custom';
  event_action: string;
  event_label?: string;
  event_value?: number;
  metadata?: Record<string, any>;
}

export interface UseTrackingReturn {
  trackPageView: (...args: any[]) => Promise<void>;
  trackEvent: (...args: any[]) => Promise<void>;
  trackSessionStart: (...args: any[]) => Promise<void>;
  trackSessionEnd: (...args: any[]) => Promise<void>;
  trackItemView: (...args: any[]) => Promise<void>;
  trackSearch: (...args: any[]) => Promise<void>;
  trackFilter: (...args: any[]) => Promise<void>;
  trackError: (...args: any[]) => Promise<void>;
  trackPerformance: (...args: any[]) => Promise<void>;
  trackLogin?: (...args: any[]) => Promise<void>;
  trackLogout?: (...args: any[]) => Promise<void>;
  trackChat?: (...args: any[]) => Promise<void>;
}

export function useTracking(): UseTrackingReturn {
  const trackLogin = useCallback(async (user_id: number, user_type: string = 'dealer'): Promise<void> => {
    try {
      const session_id = sessionStorage.getItem('bt_session_id') || (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'session-' + Date.now() + '-' + Math.random().toString(36).slice(2));
      sessionStorage.setItem('bt_session_id', session_id);
      
      await api.post('/track/session/login', {
        session_id,
        user_id,
        user_type,
        device_type: /mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
        browser: navigator.userAgent.split(')')[0].split('(')[1] || navigator.userAgent,
        os: navigator.platform,
        screen_resolution: `${screen.width}x${screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
        language: navigator.language,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        user_agent: navigator.userAgent,
        referrer: document.referrer,
        entry_page: window.location.pathname,
        login_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('trackLogin error:', error);
    }
  }, []);

  const trackLogout = useCallback(async (session_id?: string): Promise<void> => {
    try {
      const sid = session_id || sessionStorage.getItem('bt_session_id');
      if (!sid) return;
      
      await api.post('/track/session/logout', {
        session_id: sid,
        logout_at: new Date().toISOString()
      });
      sessionStorage.removeItem('bt_session_id');
    } catch (error) {
      console.error('trackLogout error:', error);
    }
  }, []);

  const trackPageView = useCallback(async (page_path: string, page_url?: string, page_type?: string): Promise<void> => {
    // No tracking storage - endpoint exists but not critical for MVP
  }, []);

  const trackEvent = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackSessionStart = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackSessionEnd = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackItemView = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackSearch = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackFilter = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackError = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackPerformance = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  const trackChat = useCallback(async (...args: any[]): Promise<void> => {
    // No tracking storage
  }, []);

  return {
    trackPageView,
    trackEvent,
    trackSessionStart,
    trackSessionEnd,
    trackItemView,
    trackSearch,
    trackFilter,
    trackError,
    trackPerformance,
    trackLogin,
    trackLogout,
    trackChat,
  };
}
